/**
 * @file ProductsFilterBar.tsx
 * @description Фильтры реестра товаров (phase19.31, зона «Товары», макет (а)).
 *
 * С 1024 — строка: поиск · «Категория ▾» (дерево в выпадающем списке) ·
 * «Публикация ▾» · «Вид ▾»; под ней один ряд пилюль отбора без дублей «Все»
 * (SC45) и справа переключатель «Таблица / Плитки». С 1600 категории — боковой
 * панелью (SC52), выпадающий список тогда не показываем.
 * До 1024 — поиск и «Фильтры · N» + вид; пилюли и списки — в шторке (на 360
 * пять пилюль заняли бы три ряда и спрятали товары).
 *
 * Поиск применяется сам через 0,4 с после ввода — без кнопки «Найти».
 */

import { useEffect, useMemo, useState } from "react";

import { Combobox, type ComboboxOption } from "../../ui/combobox";
import { FilterChips, type FilterChip } from "../../ui/FilterChips";
import { FilterSheetButton } from "../../ui/FilterSheetButton";
import { SearchInput } from "../../ui/SearchInput";
import { SelectField, type SelectFieldOption } from "../../ui/SelectField";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import type { CatalogCategory } from "../../../lib/api-catalog";
import type { ProductKind } from "../../../lib/api-product-kinds";
import { categoryTreeOptions } from "../../../lib/catalog-category-tree";
import {
  productsSheetFilterCount,
  type ProductsChip,
  type ProductsRegistryQuery,
  type ProductsView,
} from "../../../lib/catalog-registry-query";
import { PRODUCT_SITE_FILTER_LABEL, type ProductSiteState } from "../../../lib/catalog-status";

const SEARCH_DELAY_MS = 400;

const SITE_OPTIONS: SelectFieldOption[] = [
  { value: "", label: "Любая публикация" },
  ...(Object.entries(PRODUCT_SITE_FILTER_LABEL) as [ProductSiteState, string][]).map(([value, label]) => ({ value, label })),
];

/** Подписи пилюль — данными; тон «опасно» только у «Нет в наличии». */
const CHIP_LABEL: Record<ProductsChip, { label: string; tone?: "danger" }> = {
  all: { label: "Все" },
  out: { label: "Нет в наличии", tone: "danger" },
  low: { label: "Мало" },
  nowhere: { label: "Нигде не опубликован" },
  nophoto: { label: "Без фото" },
};

interface Props {
  query: ProductsRegistryQuery;
  onChange: (patch: Partial<ProductsRegistryQuery>) => void;
  onReset: () => void;
  categories: CatalogCategory[];
  kinds: ProductKind[];
  chipCounts: Record<ProductsChip, number>;
  /** Категории показаны боковой панелью (≥1600) — выпадающий список не нужен. */
  categoryPanel: boolean;
}

export function ProductsFilterBar({ query, onChange, onReset, categories, kinds, chipCounts, categoryPanel }: Props) {
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const [search, setSearch] = useState(query.search);
  useEffect(() => setSearch(query.search), [query.search]);
  useEffect(() => {
    if (search.trim() === query.search) return;
    const timer = window.setTimeout(() => onChange({ search: search.trim() }), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search, query.search, onChange]);

  const catOptions = useMemo<ComboboxOption[]>(
    () => [{ value: "", label: "Все категории" }, ...categoryTreeOptions(categories)],
    [categories],
  );
  const kindOptions = useMemo<SelectFieldOption[]>(
    () => [{ value: "", label: "Любой вид" }, ...kinds.map((k) => ({ value: k.id, label: k.name }))],
    [kinds],
  );
  const chips: FilterChip<ProductsChip>[] = (Object.keys(CHIP_LABEL) as ProductsChip[]).map((value) => ({
    value,
    ...CHIP_LABEL[value],
    count: chipCounts[value],
  }));

  const width = (wide: string) => (compact ? "w-full" : wide);
  const selects = (
    <>
      {categoryPanel && !compact ? null : (
        <Combobox
          ariaLabel="Категория"
          options={catOptions}
          value={query.categoryId}
          onChange={(categoryId) => onChange({ categoryId: categoryId ?? "" })}
          placeholder="Категория"
          searchPlaceholder="Найти категорию"
          className={width("w-[200px]")}
        />
      )}
      <SelectField ariaLabel="Публикация" value={query.site} onChange={(site) => onChange({ site: site as ProductSiteState | "" })} options={SITE_OPTIONS} className={width("w-[192px]")} />
      <SelectField ariaLabel="Вид товара" value={query.kindId} onChange={(kindId) => onChange({ kindId })} options={kindOptions} className={width("w-[168px]")} />
    </>
  );
  const chipRow = <FilterChips aria-label="Отбор товаров" items={chips} value={query.chip} onChange={(chip) => onChange({ chip })} />;
  const view = (
    <FilterChips<ProductsView>
      aria-label="Вид списка"
      items={[
        { value: "table", label: compact ? "Список" : "Таблица" },
        { value: "tiles", label: "Плитки" },
      ]}
      value={query.view}
      onChange={(next) => onChange({ view: next })}
    />
  );
  const searchField = (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onChange({ search: search.trim() });
      }}
    >
      <SearchInput value={search} onChange={setSearch} placeholder="Название или артикул" ariaLabel="Поиск товара" />
    </form>
  );

  if (compact) {
    const sheetCount = productsSheetFilterCount(query) + (query.chip !== "all" ? 1 : 0);
    return (
      <div className="flex flex-col gap-3">
        {searchField}
        <div className="flex items-center justify-between gap-2">
          <FilterSheetButton count={sheetCount} onReset={onReset}>
            {chipRow}
            {selects}
          </FilterSheetButton>
          {view}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 max-w-[360px] flex-[1_1_240px]">{searchField}</div>
        {selects}
      </div>
      {/* Вид «Таблица / Плитки» — справа в ряду пилюль: в строке поиска на 1280 ему не хватало места. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {chipRow}
        {view}
      </div>
    </div>
  );
}
